import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { usePrefersReducedMotion } from './use-media.js';

export interface ScrollStripProps {
  children: ReactNode;
  className?: string;
  /** When this changes, the element carrying aria-current or aria-selected is scrolled into view. */
  selectedKey?: string | null;
}

/** The width of an edge control (components.css `.scroll-strip__edge`), which a revealed item must clear. */
export const SCROLL_STRIP_EDGE = 44;

/**
 * Whether the selected item must be scrolled into view (pure, pinned under node): it is out of the frame, or under an
 * edge control that is drawn — `startInset`/`endInset` are the control's width where the strip continues on that side
 * and 0 where it does not, so an item flush against an exhausted edge is left alone and a strip that fits never
 * scrolls the page.
 */
export function stripRevealNeeded(box: { left: number; right: number }, frame: { left: number; right: number }, insets: { start: number; end: number }): boolean {
  return box.left < frame.left + insets.start || box.right > frame.right - insets.end;
}

/**
 * A scroll position clamped to the strip's range, and snapped to an end when it would stop within an edge control's
 * width of it (pure, pinned under node). Why the snap: a strip resting a few pixels from its start draws the start
 * chevron over the first item's label to announce those few pixels — the label is cut for nothing; at the end itself
 * no control is drawn and the item is whole.
 *
 * Why the nearer end when both apply (Phase 1c finding R20): a strip whose whole range is shorter than two controls —
 * 26 px at 412 px wide on the Assistant — has every position within a control's width of BOTH ends. Taking the start
 * first sent every reveal to 0, so the selected last tab stayed 22 px past the frame under the end chevron; the nearer
 * end sends a reveal that has to travel right to the end, and one that has to travel left to the start.
 */
export function snapToStripEnd(target: number, max: number, edge = SCROLL_STRIP_EDGE): number {
  const clamped = Math.max(0, Math.min(max, target));
  const nearStart = clamped < edge;
  const nearEnd = clamped > max - edge;
  if (nearStart && nearEnd) return clamped * 2 < max ? 0 : max;
  if (nearStart) return 0;
  if (nearEnd) return max;
  return Math.round(clamped);
}

/**
 * Where the strip scrolls to reveal the selected item (pure, pinned under node): the item CENTRED in the frame, so it is
 * clear of both edge controls with room for a count badge that lands after its fetch; clamped and snapped by
 * `snapToStripEnd`, so the first item rests flush at the start and the last at the end, where no control is drawn.
 * Why centring and not "nearest": nearest brought the deep-linked tab exactly to the frame's edge, and the browser's
 * `scroll-padding` did not reliably hold it clear of the chevron drawn over that edge — the tab's label was whole and
 * its badge was under the control.
 */
export function stripRevealTarget(box: { left: number; right: number }, frame: { left: number; right: number }, scroll: { left: number; max: number }, edge = SCROLL_STRIP_EDGE): number {
  const boxCentre = (box.left + box.right) / 2;
  const frameCentre = (frame.left + frame.right) / 2;
  return snapToStripEnd(scroll.left + (boxCentre - frameCentre), scroll.max, edge);
}

/**
 * Where a press on an edge control scrolls to (pure, pinned under node): 60% of the view, so part of what was visible
 * remains as a landmark (§8.1), snapped to the end when it would stop within a control's width of it.
 */
export function stripNudgeTarget(scroll: { left: number; max: number; width: number }, direction: -1 | 1, edge = SCROLL_STRIP_EDGE): number {
  return snapToStripEnd(scroll.left + direction * scroll.width * 0.6, scroll.max, edge);
}

/**
 * A horizontal row that scrolls rather than wraps, with 03-components §8.1's edge indication: a
 * pressable chevron on each edge that has content behind it, gone when the edge is exhausted, out
 * of the tab order (focus already moves through the row's own items), 44px targets, and a press
 * that leaves part of the previous view on screen.
 */
export function ScrollStrip({ children, className, selectedKey }: ScrollStripProps) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const reducedMotion = usePrefersReducedMotion();

  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    const max = element.scrollWidth - element.clientWidth;
    const position = Math.round(element.scrollLeft);
    setEdges((current) => {
      const next = { start: max > 1 && position > 1, end: max > 1 && position < max - 1 };
      return current.start === next.start && current.end === next.end ? current : next;
    });
  }, []);

  useLayoutEffect(() => {
    measure();
    const element = scroller.current;
    if (!element) return;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    for (const child of Array.from(element.children)) observer?.observe(child);
    element.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, children]);

  // The selected item is brought into view, centred and so clear of both edge controls (`stripRevealTarget`), when the
  // selection changes AND when the row changes afterwards — a tab's count lands after its fetch and widens it, or an
  // earlier tab's does and shifts it, which once left the selected tab's end under the chevron. The strip scrolls
  // itself (`scrollTo`), never the page: a deep link to a tab is not a reason to move the reader down to the strip.
  const reveal = useCallback((behavior: ScrollBehavior) => {
    const element = scroller.current;
    if (!element) return;
    const selected = element.querySelector<HTMLElement>('[aria-current], [aria-selected="true"]');
    if (!selected) return;
    const box = selected.getBoundingClientRect();
    const frame = element.getBoundingClientRect();
    const max = element.scrollWidth - element.clientWidth;
    const position = Math.round(element.scrollLeft);
    const insets = { start: max > 1 && position > 1 ? SCROLL_STRIP_EDGE : 0, end: max > 1 && position < max - 1 ? SCROLL_STRIP_EDGE : 0 };
    if (!stripRevealNeeded(box, frame, insets)) return;
    const target = stripRevealTarget(box, frame, { left: element.scrollLeft, max });
    if (Math.abs(target - element.scrollLeft) < 1) return;
    element.scrollTo({ left: target, behavior });
  }, []);

  useEffect(() => {
    if (selectedKey === undefined) return;
    const behavior: ScrollBehavior = reducedMotion ? 'auto' : 'smooth';
    reveal(behavior);
    const element = scroller.current;
    const selected = element?.querySelector<HTMLElement>('[aria-current], [aria-selected="true"]');
    if (!element || !selected || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => reveal(behavior));
    observer.observe(selected);
    // Why the row too: a count landing on a tab BEFORE the selected one widens the row and pushes the selected tab
    // along without changing its own box, so observing the tab alone missed it.
    for (const child of Array.from(element.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [selectedKey, reducedMotion, reveal]);

  const nudge = (direction: -1 | 1): void => {
    const element = scroller.current;
    if (!element) return;
    const max = element.scrollWidth - element.clientWidth;
    element.scrollTo({ left: stripNudgeTarget({ left: element.scrollLeft, max, width: element.clientWidth }, direction), behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <div className={['scroll-strip', className].filter(Boolean).join(' ')} data-edge-start={edges.start || undefined} data-edge-end={edges.end || undefined}>
      <button type="button" className="scroll-strip__edge scroll-strip__edge--start" aria-hidden="true" tabIndex={-1} onClick={() => nudge(-1)}>
        <Icon name="chevron-left" size={18} />
      </button>
      <div ref={scroller} className="scroll-strip__scroller">
        {children}
      </div>
      <button type="button" className="scroll-strip__edge scroll-strip__edge--end" aria-hidden="true" tabIndex={-1} onClick={() => nudge(1)}>
        <Icon name="chevron-right" size={18} />
      </button>
    </div>
  );
}
